import { plainToClass, plainToClassFromExist } from "class-transformer";
import { Arrays } from "./arrays";
import { ClassType } from "./class-type";
import { KeyValuePair } from "./key-value-pair.model";
import { OrdinaryObject, OrdinaryObjectNumber } from "./ordinary-object.model";
import { Strings } from "./strings";
import { Utils } from "./utils";
import { Comparable } from "./comparable";
import { Dates } from "./dates";
import clone from 'clone';

export class ObjectsInternal {

    static PROPERTY_NOT_FOUND = Symbol('property_not_found');

    addMissingFields(dest: OrdinaryObject, source: OrdinaryObject): void {
        for (let fieldName of this.getPropertyNames(source)) {
            if (!this.hasProperty(dest, fieldName)) {
                dest[fieldName] = source[fieldName];
            }
            if (this.is(dest[fieldName])) {
                this.addMissingFields(dest[fieldName] as OrdinaryObject, source[fieldName] as OrdinaryObject);
            }
        }
    }

    castOrNull<T>(obj: any, type: ClassType<T>): T {
        return obj instanceof type ? obj : null;
    }

    castOrThrow<T>(obj: any, type: ClassType<T>, allowNull = true): T {
        if (obj == null) {
            if (allowNull) {
                return null;
            }
            else {
                throw new Error("Das null-Objekt kann nicht in den Typ " + type + " gecastet werden");
            }
        }
        else if (obj instanceof type) {
            return obj;
        }
        else {
            throw new TypeError("Das Objekt " + this.toStringWithType(obj, 100) + " kann nicht in den Typ " + type + " gecastet werden");
        }
    }

    cloneDeep<T>(obj: T): T {
        return clone(obj);
    }

    cmp(o1: any, o2: any): number {
        if (o1 == null) {
            return o2 == null ? 0 : -1;
        }
        else if (o2 == null) {
            return 1;
        }
        else if (typeof o1 === "string" && typeof o2 === "string") {
            return o1.localeCompare(o2);
        }
        else if (o1 instanceof Date && o2 instanceof Date) {
            o1 = o1.getTime();
            o2 = o2.getTime();
            return o1 < o2 ? -1 : o1 > o2 ? 1 : 0;
        }
        else if (Utils.isFunction(o1.compareTo) && Utils.isFunction(o2.compareTo)) {
            return (o1 as Comparable<any>).compareTo(o2 as Comparable<any>);
        }
        else {
            return o1 < o2 ? -1 : o1 > o2 ? 1 : 0;
        }
    }

    cmpMulti(items: any[]): number {
        if (items.length % 2 !== 0) {
            throw new Error("Cannot compare item array with length " + items.length);
        }
        for (let i = 0; i < items.length; i += 2) {
            let result = this.cmp(items[i], items[i + 1]);
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    }

    cmpMultiWithFactor(items: any[]): number {
        if (items.length % 3 !== 0) {
            throw new Error("Cannot compare item array with length " + items.length);
        }
        for (let i = 0; i < items.length / 3; i += 3) {
            let result = this.cmp(items[i], items[i + 1]);
            if (result !== 0) {
                return result * items[i + 2];
            }
        }
        return 0;
    }

    customInitialize<T extends object>(o: T): T {
        if (this.is(o) && 'customInitializer' in o) {
            (o as any).customInitializer();
        }
        return o;
    }

    equalsDeep(a: any, b: any, debugOutput?: boolean, debugPath = "this"): boolean {
        if (a === b) {
            return true;
        }

        if (a && b && typeof a == 'object' && typeof b == 'object') {
            if (a.constructor !== b.constructor) {
                if (debugOutput) {
                    console.log("equalsDeep: Änderung gefunden: " + debugPath + ".constructor");
                }
                return false;
            }

            let length;
            if (Array.isArray(a)) {
                length = a.length;
                if (length != b.length) {
                    if (debugOutput) {
                        console.log("equalsDeep: Änderung gefunden: " + debugPath + ".length");
                    }
                    return false;
                }
                for (let i = length; i-- !== 0;) {
                    if (!this.equalsDeep(a[i], b[i], debugOutput, debugOutput ? debugPath + "[" + i + "]" : debugPath)) {
                        if (debugOutput) {
                            console.log("equalsDeep: Änderung gefunden: " + debugPath);
                        }
                        return false;
                    }
                }
                return true;
            }


            if (a instanceof Map && b instanceof Map) {
                if (a.size !== b.size) {
                    if (debugOutput) {
                        console.log("equalsDeep: Änderung gefunden: " + debugPath + ".size");
                    }
                    return false;
                }
                for (let i of a.entries()) {
                    if (!b.has(i[0])) {
                        if (debugOutput) {
                            console.log("equalsDeep: Änderung gefunden: " + debugPath + "." + i[0]);
                        }
                        return false;
                    }
                }
                for (let i of a.entries()) {
                    if (!this.equalsDeep(i[1], b.get(i[0]))) {
                        if (debugOutput) {
                            console.log("equalsDeep: Änderung gefunden: " + debugPath + "." + i[0]);
                        }
                        return false;
                    }
                }
                return true;
            }

            if (a instanceof Set && b instanceof Set) {
                if (a.size !== b.size) {
                    if (debugOutput) {
                        console.log("equalsDeep: Änderung gefunden: " + debugPath + ".size");
                    }
                    return false;
                }
                for (let i of a.entries()) {
                    if (!b.has(i[0])) {
                        if (debugOutput) {
                            console.log("equalsDeep: Änderung gefunden: " + debugPath + "." + i[0]);
                        }
                        return false;
                    }
                }
                return true;
            }

            if (a.constructor === RegExp) {
                let result = a.source === b.source && a.flags === b.flags;
                if (debugOutput) {
                    console.log("equalsDeep: Änderung gefunden: " + debugPath);
                }
                return result;
            }
            if (a.valueOf !== Object.prototype.valueOf) {
                let result = a.valueOf() === b.valueOf();
                if (debugOutput) {
                    console.log("equalsDeep: Änderung gefunden: " + debugPath);
                }
                return result;
            }

            let keys = Object.keys(a as OrdinaryObject);
            length = keys.length;
            if (length !== Object.keys(b as OrdinaryObject).length) {
                if (debugOutput) {
                    console.log("equalsDeep: Änderung gefunden: " + debugPath + ".keys.length");
                }
                return false;
            }

            for (let i = length; i-- !== 0;) {
                if (!Object.prototype.hasOwnProperty.call(b, keys[i])) {
                    if (debugOutput) {
                        console.log("equalsDeep: Änderung gefunden: " + debugPath + "." + keys[i]);
                    }
                    return false;
                }
            }
            for (let i = length; i-- !== 0;) {
                if (!this.equalsDeep(a[keys[i]], b[keys[i]])) {
                    if (debugOutput) {
                        console.log("equalsDeep: Änderung gefunden: " + debugPath + "." + keys[i]);
                    }
                    return false;
                }
            }

            return true;
        }

        return Number.isNaN(a as number) && Number.isNaN(b as number);
    }

    filter(obj: OrdinaryObject, acceptor: (key: string, value: any) => boolean): OrdinaryObject {
        let result: OrdinaryObject = {};
        for (let propName of this.getPropertyNames(obj)) {
            if (acceptor(propName, obj[propName])) {
                result[propName] = obj[propName];
            }
        }
        return result;
    }

    flattenObjectSimple(o: OrdinaryObject): OrdinaryObject {
        let result: OrdinaryObject = {};

        for (let i in o) {
            if (!this.hasProperty(o, i)) {
                continue;
            }

            if (typeof o[i] == 'object' && o[i] !== null) {
                let flatObject = this.flattenObjectSimple(o[i] as OrdinaryObject);
                for (let x in flatObject) {
                    if (!this.hasProperty(flatObject, x)) {
                        continue;
                    }

                    result[i + '.' + x] = flatObject[x];
                }
            } else {
                result[i] = o[i];
            }
        }
        return result;
    }

    fromJson(json: string): any {
        if (!Strings.is(json)) {
            throw new Error("Fehler beim Parsen des JSONS. Die Eingabe ist kein String, sondern " + Objects.toStringWithType(json));
        }
        try {
            return JSON.parse(json);
        }
        catch (error) {
            let posString = Strings.getSubstringFrom((error as Error).message, "at position ", false);
            if (!Utils.isNoe(posString)) {
                let pos = Utils.toNumber(posString);
                if (pos >= 0) {
                    let [row, col] = Strings.getRowAndColFromPos(json, pos);
                    throw new Error("Fehler beim Parsen des JSONs an Index " + pos + " (" + row + ":" + col + ") Orginal-Fehlermeldung: " + (error as Error).message);
                }
            }
            throw error;
        }
    }

    fromJsonIfString(json: any): any {
        return Strings.is(json) ? this.fromJson(json as string) : json;
    }

    fromPlainUnsafe<T extends object>(type: ClassType<T>, plain: any, excludeExtraneousValues = false): T {
        return this.customInitialize(plainToClass(type, plain, { enableCircularCheck: true, excludeExtraneousValues }));
    }

    fromPlainArray<T extends object>(type: ClassType<T>, plain: any[]): T[] {
        return plain.map(item => this.fromPlainUnsafe(type, item));
    }

    fromPlainFill<T>(existing: T, plain: any): T {
        return plainToClassFromExist(existing, plain);
    }

    fromPlain<T extends object>(cls: ClassType<T>, plain: Partial<T>, excludeExtraneousValues = false): T {
        return this.fromPlainUnsafe(cls, plain, excludeExtraneousValues);
    }

    getPropertyNames(obj: any): string[] {
        let result = [];

        for (let prop in obj) {
            if (this.hasProperty(obj, prop)) {
                result.push(prop);
            }
        }
        return result;
    }

    getOrAdd<V>(map: OrdinaryObject<V>, key: string, generator: () => V): V {
        let existing: V;
        if (!this.hasProperty(map, key)) {
            existing = generator();
            map[key] = existing;
        }
        else {
            existing = map[key];
        }
        return existing;
    }

    async getOrAddAsync<V>(map: OrdinaryObject<V>, key: string, generator: () => Promise<V>): Promise<V> {
        let existing: V;
        if (!this.hasProperty(map, key)) {
            existing = await generator();
            map[key] = existing;
        }
        else {
            existing = map[key];
        }
        return existing;
    }

    //eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    getProperty<T>(on: any, propName: string, fail = false): T {
        if (this.hasProperty(on, propName)) {
            return on[propName] as T;
        }
        else if (fail) {
            throw new Error("Die Eigenschaft " + propName + " wurde nicht gefunden in Objekt" + this.toJson(on, false));
        }
        else {
            return null;
        }
    }

    getPropertyDef<T>(on: any, propName: string, def: T): T {
        return this.hasProperty(on, propName) ? on[propName] as T : def;
    }

    getPropertyNested(on: any, propertyName: string, returnPropertyNotFound?: boolean): any {
        for (let part of this.getPropertyPath(propertyName)) {
            if (on == null) {
                return returnPropertyNotFound ? ObjectsInternal.PROPERTY_NOT_FOUND : null;
            }
            let propName = part;
            let arrayIndex = Strings.regexGetGroup(propName, /.*\[(\d+)]$/g, 1);
            if (arrayIndex != null) {
                propName = Strings.regexGetGroup(propName, /(.*)\[\d+]$/g, 1);
            }
            if (!this.hasProperty(on, propName)) {
                return returnPropertyNotFound ? ObjectsInternal.PROPERTY_NOT_FOUND : null;
            }
            let v = on[propName];
            on = arrayIndex != null && Arrays.is(v) ? Arrays.getSafe(v as any[], Utils.toNumber(arrayIndex)) : v;
        }
        return on;
    }

    getPropertyPath(propertyName: string): string[] {
        const result: string[] = [];
        let part = "";
        let escaped = false;

        for (const char of propertyName) {
            if (escaped) {
                part += char;
                escaped = false;
                continue;
            }

            if (char === "\\") {
                escaped = true;
                continue;
            }

            if (char === ".") {
                result.push(part);
                part = "";
                continue;
            }

            part += char;
        }

        if (escaped) {
            part += "\\";
        }

        result.push(part);
        return result;
    }

    escapeProperty(propertyName: string): string {
        return propertyName.replace(/\./g, "\\.")
    }

    getSafe<T>(on: OrdinaryObject<T> | OrdinaryObjectNumber<T>, paramName: string | number, def: T = null): T {
        return this.hasProperty(on, paramName) ? (on as any)[paramName] : def;
    }

    getTypeName(instance: object): string {
        if (instance == null) {
            return null;
        }
        let result = instance.constructor.name;
        if (result === "Function" && this.hasProperty(instance, "prototype")) {
            result = (instance as any).prototype.constructor.name;
        }
        return result;
    }

    hasAnyProperty(obj: any): boolean {
        for (let prop in obj) {
            if (this.hasProperty(obj, prop)) {
                return true;
            }
        }
        return false;
    }


    hasProperty(on: any, name: PropertyKey): boolean {
        if (on == null) {
            throw new Error("hasProperty(on '" + this.toString(name) + "') nicht nicht ausgeführt werden, da on null ist");
        }
        return Object.prototype.hasOwnProperty.call(on, name);
    }

    is(object: any): boolean {
        return typeof object === 'object' && object !== null;
    }

    getValues<X>(obj: OrdinaryObject<X>): X[] {
        return this.getPropertyNames(obj).map(key => obj[key]);
    }

    mergeDeep(objects: any[]): any {
        const result: any = {};
        for (const obj of objects) {
            if (!obj || typeof obj !== 'object') {
                continue;
            }

            for (const key of Object.keys(obj as object)) {
                const value = obj[key];

                result[key] = value && typeof value === 'object' && !Arrays.is(value) && typeof result[key] === 'object' && !Arrays.is(result[key]) ? this.mergeDeep([result[key], value]) : value;
            }
        }

        return result;
    }

    propertiesToKeyValuePairs(o: OrdinaryObject): KeyValuePair<string, string>[] {
        return this.getPropertyNames(o).map(key => new KeyValuePair<string, string>(key, o[key] as string));
    }

    removeNullValues<T extends OrdinaryObject>(obj: T, recursive?: boolean): T {
        for (let propName of this.getPropertyNames(obj)) {
            if (obj[propName] == null) {
                delete obj[propName];
            }
            else if (recursive && this.is(obj[propName])) {
                this.removeNullValues(obj[propName], recursive);
            }
        }
        return obj;
    }

    setPropertyNested(on: any, propertyName: string, value: any, createSubObjects?: boolean): boolean {
        let parts = propertyName.split(".");
        for (let i = 0; i < parts.length - 1; i++) {
            if (!this.hasProperty(on, parts[i])) {
                if (createSubObjects) {
                    on[parts[i]] = {};
                }
                else {
                    return false;
                }
            }
            on = on[parts[i]];
        }
        on[parts[parts.length - 1]] = value;
        return true;
    }

    toJson(obj: any, pretty = false): string {
        const ancestors: any[] = [];

        const replacer = function(this: any, key: any, value: any): any {
            if (typeof value !== "object" || value == null) {
                return value;
            }

            while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
                ancestors.pop();
            }

            if (ancestors.includes(value)) {
                return null;
            }

            ancestors.push(value);
            return value;
        };

        obj = obj === undefined ? null : obj;
        return pretty ? JSON.stringify(obj, replacer, 2) : JSON.stringify(obj, replacer);
    }

    toString(object: any): string {
        return object == null ? "null" :
            Strings.is(object) ? object as string :
            Utils.isNumber(object) || Dates.is(object) ? object.toString() :
            Utils.isFunction(object.getStringRepresentation) ?
            object.getStringRepresentation() :
            this.toJson(object);
    }

    toStringWithType(object: any, maxLength?: number): string {
        let s = this.toString(object);
        if (maxLength != null) {
            s = Strings.crop(s, maxLength, true);
        }
        return s + " (Datentyp: " + Objects.getTypeName(object as object) + ")";
    }

    unflatten(flat: OrdinaryObject): any {
        const result: any = {};

        for (const flatKey in flat) {
          const value = flat[flatKey];
          const path = flatKey.split('.');

          let current = result;

          for (let i = 0; i < path.length; i++) {
            const key = path[i];
            const isLast = i === path.length - 1;

            if (isLast) {
              current[key] = value;
            } else {
              if (!(key in current)) {
                current[key] = {};
              }

              current = current[key];
            }
          }
        }

        return result;
    }

    unrollJsonObject(o: OrdinaryObject): OrdinaryObject {
        for (let propName of this.getPropertyNames(o)) {
            let parts = Strings.splitOnce(propName, ".");
            if (parts.length == 2) {
                if (o[parts[0]] == null) {
                    o[parts[0]] = {};
                }
                o[parts[0]][parts[1]] = o[propName];
                delete o[propName];
            }
        }
        for (let propName of this.getPropertyNames(o)) {
            if (this.is(o[propName])) {
                this.unrollJsonObject(o[propName] as OrdinaryObject);
            }
        }
        return o;
    }

    with<T, K extends keyof T>(obj: T, propertyName: K, value: T[K]): T {
        obj[propertyName] = value;
        return obj;
    }

    without<T extends OrdinaryObject>(obj: T, remove: string[]): T {
        let result = {...obj};
        for (let propName of remove) {
            delete result[propName];
        }
        return result;
    }


}

export let Objects = new ObjectsInternal();